import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField } from '@/components/forms';
import { onlyEnabled } from '@/config/preview-features';
import { deviceTypeNoun } from '@/features/devices';
import { appTzLabel, fromDate, isAfterAppToday, toDayjs } from '@/lib/date';

import {
  deviceAlertHasActiveFilters,
  deviceAlertToDefaults,
  deviceAlertToFilters,
} from '../device-alert-filters';
import { DEVICE_ALERT_TYPE_LABEL, DEVICE_TYPE_OPTIONS } from '../format';
import { useCustomerDeviceAlertFilterOptions } from '../hooks';
import {
  customerDeviceAlertFiltersSchema,
  DEVICE_ALERT_DATE_ORDER_ERROR,
  deviceAlertTypeSchema,
} from '../schemas';
import type { CustomerDeviceAlertFilterFormValues, CustomerDeviceAlertFilters } from '../types';

import styles from './customer-device-alert-filter-drawer.module.scss';

const DATE_LABEL = appTzLabel('Log Date Range');
const ALERT_TYPE_OPTIONS = deviceAlertTypeSchema.options.map((value) => ({
  value,
  label: DEVICE_ALERT_TYPE_LABEL[value],
}));

export type CustomerDeviceAlertFilterDrawerProps = {
  open: boolean;
  customerId: string;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: CustomerDeviceAlertFilters;
  onApply: (filters: CustomerDeviceAlertFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<CustomerDeviceAlertFilterDrawerProps, 'open'>;

function FilterBody({ customerId, value, onApply, onClose }: BodyProps) {
  const draft = deviceAlertToDefaults(value);
  const form = useForm<CustomerDeviceAlertFilterFormValues>({
    resolver: zodResolver(customerDeviceAlertFiltersSchema),
    defaultValues: {
      ...draft,
      logDateFrom: draft.logDateFrom ?? null,
      logDateTo: draft.logDateTo ?? null,
    },
    mode: 'onChange',
  });
  const [alertTypes, deviceIds, deviceType, category, from, to] = useWatch({
    control: form.control,
    name: ['alertTypes', 'deviceIds', 'deviceType', 'category', 'logDateFrom', 'logDateTo'],
  });

  const options = useCustomerDeviceAlertFilterOptions(customerId);
  const deviceOptions = useMemo(
    () => (options.data?.devices ?? []).map((d) => ({ value: d.id, label: d.name })),
    [options.data],
  );
  const categoryOptions = useMemo(
    () => (options.data?.categories ?? []).map((c) => ({ value: c, label: c })),
    [options.data],
  );
  // 118646: DM Device, and CropX Device only where CropX is in scope (ADO 121238). Never Other.
  const deviceTypeOptions = onlyEnabled(DEVICE_TYPE_OPTIONS)
    .filter((o) => o.value !== 'other')
    .map((o) => ({ value: o.value, label: deviceTypeNoun(o.value) }));

  // AC: a TYPED To before the From. rc-picker never commits it (the date is disabled), so it can
  // only be seen through onCalendarChange — with `order={false}`, or rc-picker re-sorts the pair.
  // Verified by probe (plan.md decision 1). ticket 10 (History) carries the same rule; lift this
  // fieldset into a shared component there rather than copying it a third time.
  const [inverted, setInverted] = useState(false);
  const activeEnd = useRef<'start' | 'end' | undefined>(undefined);
  const dateError = inverted
    ? DEVICE_ALERT_DATE_ORDER_ERROR
    : form.formState.errors.logDateTo?.message;

  const nothingSelected = !deviceAlertHasActiveFilters(
    deviceAlertToFilters({
      alertTypes,
      deviceIds,
      deviceType,
      category,
      logDateFrom: from,
      logDateTo: to,
    }),
  );

  const handleApply = form.handleSubmit((v) => {
    onApply(deviceAlertToFilters(v));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={nothingSelected || Boolean(dateError)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="alertTypes"
        control={form.control}
        label="Alert Type"
        placeholder="Select"
        showSearch
        options={ALERT_TYPE_OPTIONS}
      />
      <MultiSelectField
        name="deviceIds"
        control={form.control}
        label="Device"
        placeholder="Select"
        showSearch
        loading={options.isLoading}
        options={deviceOptions}
      />
      <MultiSelectField
        name="deviceType"
        control={form.control}
        label="Device Type"
        placeholder="Select"
        showSearch
        options={deviceTypeOptions}
      />
      <MultiSelectField
        name="category"
        control={form.control}
        label="Device Category"
        placeholder="Select"
        showSearch
        loading={options.isLoading}
        options={categoryOptions}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{DATE_LABEL}</legend>
        <DateRangePicker
          aria-label={DATE_LABEL}
          format="MM/DD/YYYY"
          style={{ width: '100%' }}
          allowEmpty={[true, true]}
          order={false}
          status={dateError ? 'error' : undefined}
          onFocus={(_, info) => {
            activeEnd.current = info.range;
          }}
          disabledDate={(current, info) => {
            if (isAfterAppToday(current)) return true;
            const start = info.from ?? (activeEnd.current === 'end' ? toDayjs(from) : undefined);
            return start != null && current.isBefore(start, 'day');
          }}
          onCalendarChange={(range) =>
            setInverted(Boolean(range?.[0] && range?.[1] && range[1].isBefore(range[0], 'day')))
          }
          value={[toDayjs(from) ?? null, toDayjs(to) ?? null]}
          onChange={(range) => {
            setInverted(false);
            form.setValue('logDateFrom', fromDate(range?.[0]) ?? null, { shouldValidate: true });
            form.setValue('logDateTo', fromDate(range?.[1]) ?? null, { shouldValidate: true });
          }}
        />
        {dateError ? <p className={styles.error}>{dateError}</p> : null}
      </fieldset>
    </Drawer>
  );
}

export function CustomerDeviceAlertFilterDrawer({
  open,
  ...rest
}: CustomerDeviceAlertFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open, so Cancel discards the draft (AC2).
  return <FilterBody {...rest} />;
}
